import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAtomValue } from 'jotai/utils';
import { chatMessagesAtom, chatStreamingAtom, chatSessionOriginAtom, chatSessionScheduleIdAtom } from 'src/shared/chat';
import { UserMessage } from './UserMessage';
import { AssistantMessage } from './AssistantMessage';
import { WorkingIndicator } from './WorkingIndicator';
import { ChatEmptyState } from './emptyState';
import styles from './MessageList.module.scss';

export const MessageList: React.FC = () => {
  const messages = useAtomValue(chatMessagesAtom);
  const isStreaming = useAtomValue(chatStreamingAtom);
  const sessionOrigin = useAtomValue(chatSessionOriginAtom);
  const scheduleId = useAtomValue(chatSessionScheduleIdAtom);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isStreaming]);

  // Between sending and the first event of the reply there is no assistant
  // message yet to carry the indicator.
  const awaitingReply = isStreaming && messages[messages.length - 1]?.role === 'user';

  if (messages.length === 0) {
    return (
      <div className={styles.container}>
        <ChatEmptyState />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {sessionOrigin === 'scheduled' && scheduleId && (
        <Link to={`/ai/schedules/${scheduleId}`} className={styles.scheduledBanner}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" />
            <path d="M3 9h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          Scheduled run — open this agent
        </Link>
      )}
      <div className={styles.messagesList}>
        {messages.map((msg, idx) =>
          msg.role === 'user' ? (
            <UserMessage key={msg.id} message={msg} />
          ) : (
            <AssistantMessage key={msg.id} message={msg} isLast={idx === messages.length - 1} />
          ),
        )}
        {awaitingReply && <WorkingIndicator />}
        <div ref={bottomRef} />
      </div>
    </div>
  );
};
